import api from './api';
import { ensureTenantId } from '../utils/tenantUtils';

// Handle JWT token expiration
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // Get the expiration from token
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    
    // Check if expired
    const currentTime = Date.now() / 1000;
    return payload.exp && payload.exp < currentTime;
  } catch (e) {
    console.error('Error checking token expiration:', e);
    return true;
  }
};

/**
 * Sleep utility for implementing delay between retries
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise} - Promise that resolves after the specified time
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Login function with password reset check
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise} - Promise with user data, token, and passwordResetRequired flag
 */
const login = async (email, password) => {
  let retryCount = 0;
  const maxRetries = 3;
  const initialDelayMs = 2000; // Start with a 2 second delay

  const attemptLogin = async () => {
    try {
      console.log(`authService: Attempting login... (attempt ${retryCount + 1}/${maxRetries + 1})`, { email });
      const { data: response } = await api.post('/auth/login', { email, password });
      
      // Detailed raw response logging for troubleshooting
      console.log('authService: Raw server response:', response);
      console.log('authService: Response type:', typeof response);
      
      // Handle case where response is a string "Startup in progress"
      if (typeof response === 'string') {
        // Check specifically for startup message
        if (response.includes('Startup in progress')) {
          console.log('authService: Server is still starting up');
          
          if (retryCount < maxRetries) {
            // Calculate exponential backoff delay with jitter
            const delayMs = initialDelayMs * Math.pow(2, retryCount) + Math.random() * 1000;
            console.log(`authService: Retrying in ${Math.round(delayMs/1000)} seconds...`);
            
            retryCount++;
            await sleep(delayMs);
            return await attemptLogin(); // Retry recursively
          } else {
            throw new Error('Server is still starting up. Please try again in a few moments.');
          }
        }

        // For other string responses, try to parse as JSON
        try {
          const parsedResponse = JSON.parse(response);
          console.log('authService: Parsed string response:', parsedResponse);
          if (parsedResponse) {
            // Use the parsed response instead
            return parsedResponse;
          }
        } catch (parseError) {
          console.error('authService: Failed to parse string response:', parseError);
          // If the message is about startup, provide a more user-friendly error
          if (response.includes('start') || response.includes('boot') || response.includes('initializing')) {
            throw new Error('The system is still initializing. Please try again shortly.');
          }
          // For other unrecognized string responses
          throw new Error(`Unexpected server response: ${response}`);
        }
      }

      // Handle different success indicators
      const isSuccess = response.success !== false; // Consider successful unless explicitly false
      
      if (!isSuccess) {
        throw new Error(response.message || response.error || 'Login failed');
      }

      return response;
    } catch (error) {
      // Extract response data if available
      const responseData = error.response?.data;

      // Enhanced handling for 500 errors specifically during login
      if (error.response?.status === 500) {
        console.error('authService: Server returned 500 error:', error.response?.data);
        
        // Check for specific issues we can handle
        if (error.isStartupError) {
          console.log('authService: Server is starting up, will retry automatically');
          
          if (retryCount < maxRetries) {
            const delayMs = initialDelayMs * Math.pow(2, retryCount) + Math.random() * 1000;
            console.log(`authService: Retrying in ${Math.round(delayMs/1000)} seconds...`);
            
            retryCount++;
            await sleep(delayMs);
            return await attemptLogin(); // Retry recursively
          }
        }
        
        // Extract any useful error message from the server response
        const serverMessage = responseData?.message || responseData?.error;
        if (serverMessage) {
          console.error('authService: Server error message:', serverMessage);
          throw new Error(`Login failed: ${serverMessage}`);
        }
        
        // Default error for 500 responses
        throw new Error('Server error during login. Please try again later.');
      }

      // Check for specific Azure App Service warmup/startup errors
      if (
        responseData === 'Startup in progress' ||
        (typeof responseData === 'string' && responseData.includes('starting up')) ||
        (error.message && error.message.includes('Startup in progress'))
      ) {
        console.log('authService: Server startup detected through error response');
        
        if (retryCount < maxRetries) {
          // Calculate exponential backoff delay with jitter
          const delayMs = initialDelayMs * Math.pow(2, retryCount) + Math.random() * 1000;
          console.log(`authService: Retrying in ${Math.round(delayMs/1000)} seconds...`);
          
          retryCount++;
          await sleep(delayMs);
          return await attemptLogin(); // Retry recursively
        }
        
        // If we've exhausted retries, throw a friendly error
        throw new Error('The system is still starting up. Please try again in a few moments.');
      }
      
      // Rethrow any other errors
      throw error;
    }
  };

  try {
    // Start the login attempt flow, which will handle retries internally
    const response = await attemptLogin();
    
    // Enhanced extraction of user data with multiple paths
    let userData = null;
    let token = null;
    
    // Try multiple paths where user data might be located
    if (response.user) {
      userData = response.user;
    } else if (response.data && response.data.user) {
      userData = response.data.user;
    } else if (response._id || response.id || response.userId || response.email) {
      // The response itself might be the user object
      userData = response;
    }

    // Similarly find token in various locations
    if (response.token) {
      token = response.token;
    } else if (response.data && response.data.token) {
      token = response.data.token;
    } else if (response.accessToken) {
      token = response.accessToken;
    } else if (response.access_token) {
      token = response.access_token;
    }

    // Debug log the extracted data
    console.log('authService: Extracted auth data:', {
      hasUser: !!userData,
      hasToken: !!token,
      userFields: userData ? Object.keys(userData) : [],
      passwordResetRequired: 
        userData?.passwordResetRequired || 
        response.passwordResetRequired || 
        (response.data && response.data.passwordResetRequired)
    });

    if (!userData) {
      console.error('authService: User data missing in server response. Full response:', JSON.stringify(response));
      throw new Error('User data missing in server response');
    }

    if (!token) {
      console.error('authService: Authentication token missing in server response');
      throw new Error('Authentication token missing in server response');
    }

    // Ensure user has required fields
    if (!userData.role) {
      console.warn('User data missing role field:', userData);
      // Provide a default role if missing
      userData.role = 'member';
    }

    // Store authentication data in localStorage
    localStorage.setItem('token', token);
    localStorage.setItem('authUser', JSON.stringify(userData));
    localStorage.setItem('userId', userData._id || userData.id || userData.userId);
    
    if (userData.tenantId) {
      localStorage.setItem('tenantId', userData.tenantId);
    }

    // Store tenant info if available
    if (response.tenant) {
      localStorage.setItem('tenantInfo', JSON.stringify(response.tenant));
    }

    // Make sure to use the passwordResetRequired from either user object or root response
    const passwordResetRequired = !!userData.passwordResetRequired || !!response.passwordResetRequired;

    // Return both user and token and password reset flag
    return {
      user: {
        ...userData,
        passwordResetRequired // Ensure it's always included in user object
      },
      token,
      tenant: response.tenant,
      passwordResetRequired
    };
  } catch (error) {
    console.error('Login error:', error);
    // Include more context in the error for easier debugging
    if (error.response) {
      console.error('Server response error details:', {
        status: error.response.status,
        data: error.response.data
      });
    }
    throw error;
  }
};

// Update other methods to handle the axios response structure

/**
 * Handles user registration
 */
const register = async (userData) => {
  try {
    const axiosResponse = await api.post('/auth/register', userData);
    const response = axiosResponse.data;
    
    // Use the same normalization logic as login for consistency
    let result = {};
    
    if (response.user && response.token) {
      result = response;
    } else if (response.data && response.data.user && response.data.token) {
      result = response.data;
    } else if (response.token && (response._id || response.id)) {
      result = {
        user: response,
        token: response.token
      };
    } else {
      throw new Error('Invalid server response format');
    }
    
    return result;
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

/**
 * Logs out the current user
 */
const logout = async () => {
  try {
    // Call logout endpoint if it exists
    await api.post('/auth/logout');
  } catch (error) {
    console.warn('Error during logout API call:', error);
    // Continue with logout even if the API call fails
  }
  
  // Clear local storage items related to authentication
  localStorage.removeItem('token');
  localStorage.removeItem('authUser');
  localStorage.removeItem('userId');
  
  // Clear tenant-related information
  localStorage.removeItem('tenantId');
  localStorage.removeItem('currentTenant');
  localStorage.removeItem('tenantInfo');
  
  return true;
};

/**
 * Gets the current authenticated user
 * @returns {Promise<Object|null>} Current user or null if not authenticated
 */
const getCurrentUser = async () => {
  try {
    const token = localStorage.getItem('token');
    
    // If no token or token expired, return null
    if (!token || isTokenExpired(token)) {
      return null;
    }
    
    // Try to get user from localStorage first
    const storedUser = localStorage.getItem('authUser');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user && (user._id || user.id)) {
          // Make sure userId is set consistently
          localStorage.setItem('userId', user._id || user.id);
          
          // Store tenant ID if available using standard utility
          if (user.tenantId) {
            ensureTenantId(user.tenantId);
          }
          
          return user;
        }
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }
    
    // If no valid user in localStorage, fetch from API
    const axiosResponse = await api.get('/auth/me');
    const response = axiosResponse.data;
    
    if (response && response.user) {
      localStorage.setItem('authUser', JSON.stringify(response.user));
      localStorage.setItem('userId', response.user._id || response.user.id);
      
      // Store tenant ID if available using standard utility
      if (response.user.tenantId) {
        ensureTenantId(response.user.tenantId);
      }
      
      return response.user;
    } else if (response && (response._id || response.id)) {
      // The response itself is the user object
      localStorage.setItem('authUser', JSON.stringify(response));
      localStorage.setItem('userId', response._id || response.id);
      
      // Store tenant ID if available using standard utility
      if (response.tenantId) {
        ensureTenantId(response.tenantId);
      }
      
      return response;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching current user:', error);
    
    // Clear token on auth errors
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
    }
    
    return null;
  }
};

/**
 * Gets the stored user from localStorage
 */
const getStoredUser = () => {
  try {
    const storedUser = localStorage.getItem('authUser');
    if (!storedUser) return null;
    
    const user = JSON.parse(storedUser);
    return user && (user._id || user.id) ? user : null;
  } catch (error) {
    console.error('Error getting stored user:', error);
    return null;
  }
};

/**
 * Gets auth header for API requests
 */
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Gets the current authentication token
 * @returns {string|null} - The current token or null if not available/expired
 */
const getToken = () => {
  const token = localStorage.getItem('token');
  
  // Return null if token doesn't exist or is expired
  if (!token || isTokenExpired(token)) {
    return null;
  }
  
  return token;
};

const forgotPassword = async (email) => {
  try {
    const response = await api.post('/auth/forgot-password', { email });
    return response;
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
};

const resetPassword = async (token, password) => {
  try {
    const response = await api.post('/auth/reset-password', { token, password });
    return response;
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
};

/**
 * Change password method
 * @param {string} currentPassword - Current/temporary password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Response with updated user data
 */
const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await api.post('/auth/change-password', { 
      currentPassword,
      newPassword
    });
    
    // Standardize response handling
    const result = response.data;
    
    // If successful, update stored user data to reflect password reset is no longer required
    if (result.success) {
      const userData = getStoredUser();
      if (userData) {
        userData.passwordResetRequired = false;
        localStorage.setItem('authUser', JSON.stringify(userData));
      }
    }
    
    return result;
  } catch (error) {
    console.error('Change password error:', error);
    throw error;
  }
};

/**
 * Check if password reset is required for current user
 * @returns {boolean} True if password reset is required
 */
const isPasswordResetRequired = () => {
  try {
    const user = getStoredUser();
    return !!user && !!user.passwordResetRequired;
  } catch (error) {
    console.error('Error checking password reset requirement:', error);
    return false;
  }
};

const authService = {
  login,
  logout,
  register,
  getCurrentUser,
  forgotPassword,
  resetPassword,
  getStoredUser,
  getAuthHeader,
  getToken,
  changePassword,
  isPasswordResetRequired
};

export default authService;
export { authService };